import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { VariantStep } from "@/components/missions/variant-step"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Spinner } from "@/components/ui/spinner"
import { creatorCollabTransitionsPath } from "@/routes"
import type { CollabOfferedProduct } from "@/types/collab"

/**
 * OKS-936 — the creator's "I don't have it anymore" escape hatch (UX §3.3).
 *
 * # Why this confirms rather than firing on one tap
 *
 * The action moves the collab BACKWARDS, obliges the brand to ship a physical
 * unit, and changes what every future mission of that brand sees for this
 * creator (PRD D9). One affordance, one dialog, no navigation.
 *
 * # Rendering is server-gated, never re-derived
 *
 * The caller renders this iff `collab.can_void_possession` — a single
 * server-authoritative boolean. The FE never re-derives D9's five clauses; if
 * it did, the UI and the service could disagree about whether the hatch
 * applies, and the service is the one that decides.
 *
 * # ⚠️ The POST is not the guard
 *
 * `Collabs::Transition` refuses a `void_possession` that isn't permitted, and
 * `Collabs::ConfirmFlag` refuses `confirm_receipt` on a live no-ship collab.
 * Hiding this trigger is a rendering decision only — every one of those
 * vectors is reachable by replay without touching this component.
 *
 * Inertia `<Form>` (Crew rule — never shadcn `Form`/react-hook-form/zod).
 */
interface VoidPossessionDialogProps {
  collabId: number
  /** `mission_products.name` SNAPSHOT — never `products.title`. */
  productName: string
  brandName: string
  /**
   * OKS-990 R13 — the offered set, present ONLY when this void must record a
   * pick: the collab has no product recorded AND the mission offers a real
   * choice by R2 evaluated at VOID time. Absent otherwise, which is what keeps
   * the legacy one-step hatch byte-identical (UX §9 clause 4).
   */
  offer?: CollabOfferedProduct[] | null
}

export function VoidPossessionDialog({
  collabId,
  productName,
  brandName,
  offer,
}: VoidPossessionDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [productId, setProductId] = useState<string>("")
  const [variantId, setVariantId] = useState<string>("")
  const [step, setStep] = useState<"product" | "variant">("product")
  const [clientError, setClientError] = useState<string | null>(null)

  // OKS-990 R13 — the void is the other moment a collab joins the shipping
  // path, so on a multi-product drop with NO recorded pick the creator must
  // say which product is owed. `offer` is present only when that applies (the
  // server evaluates R2 FRESH at void time and omits the key otherwise), so
  // the legacy one-step hatch is byte-identical by construction — UX §9
  // clause 4 — rather than by a length check here.
  const needsPick = (offer?.length ?? 0) > 0

  const chosen = offer?.find((product) => String(product.id) === productId)

  // ⚠️ F-VOID-VARIANT — R5's cascade applies HERE TOO, and its absence was not
  // a cosmetic gap. `MissionProducts::PickResolver` runs on the void path as
  // well, so on a product with >= 2 choosable variants it returned
  // `:variant_required`, `Collabs::VoidPossession` rolled the transaction back,
  // and NO VOID COULD EVER COMMIT on a multi-variant mission. The server was
  // right; the dialog simply never asked the second question. The creator saw a
  // confirm that did nothing, on the escape hatch that exists to unblock them.
  //
  // `variant_count` is the CHOOSABLE count — archived variants are already
  // filtered server-side — so this matches `CreatorChoiceSet.step?` rather than
  // re-deriving it from `variants.length`, which counts differently.
  const needsVariantStep = (chosen?.variant_count ?? 0) >= 2

  // The legacy one-step hatch (no `offer`) submits on the first click, exactly
  // as OKS-936 shipped it — `needsPick` false short-circuits the whole cascade
  // (UX §9 clause 4). With a pick, the confirm ADVANCES until the last step.
  const isFinalStep =
    !needsPick || (chosen != null && (step === "variant" || !needsVariantStep))

  // ⚠️ OKS-1040 (PO 3.6 D1) — `preventDefault` is LOAD-BEARING, same defect
  // as `DropPickerDialog` (on main since OKS-990): this handler moves to the
  // variant step, which re-renders THIS SAME button as `type="submit"` before
  // the browser runs the click's default action, so the void would submit an
  // empty variant. Invisible to jsdom; the gate is Playwright.
  function advance(event: { preventDefault: () => void }) {
    event.preventDefault()
    if (!chosen) {
      setClientError(t("collabs.transitions.errors.product_required"))
      return
    }
    setClientError(null)
    setStep("variant")
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={
          <Button
            type="button"
            variant="link"
            size="sm"
            className="text-muted-foreground h-11 w-full justify-start px-0 sm:w-auto"
            data-testid="collab-void-possession-trigger"
          >
            {t("collabs.no_ship.hatch_cta")}
          </Button>
        }
      />
      <AlertDialogContent data-testid="collab-void-possession-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("collabs.no_ship.dialog.title", { product: productName })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("collabs.no_ship.dialog.body", { brand: brandName })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Form
          action={creatorCollabTransitionsPath(collabId)}
          method="post"
          options={{ preserveScroll: true }}
          data-testid="collab-void-possession-form"
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="void_possession" />
              {needsPick && (
                <input
                  type="hidden"
                  name="mission_product_id"
                  value={productId}
                />
              )}
              {/* Absent when the product has no real choice: `PickResolver`
                  auto-picks a lone variant and records nothing for zero, so
                  posting an empty string here would be a claim the creator
                  never made. */}
              {needsPick && needsVariantStep && (
                <input
                  type="hidden"
                  name="product_variant_id"
                  value={variantId}
                />
              )}

              {/* ⚠️ EXHAUSTED PRODUCTS STAY SELECTABLE HERE — the OPPOSITE of
                  the request-time picker, and it is the ruling, not an
                  oversight (PRD R13 / UX-A6).

                  At the REQUEST an exhausted product means "pick another"; at
                  the VOID it means "this one is coming later". The creator is
                  replacing a specific product they held, not choosing from an
                  offer, and narrowing a shipped escape hatch to enforce stock
                  would trap the creator it exists to free. The server does not
                  consult `CapacityGuard` on this path at all.

                  ⚠️ Do NOT reuse the request picker's refusal behaviour here.
                  UX-A6 names this as the single most likely thing for a
                  reviewer to "fix" into consistency. */}
              {needsPick && step === "product" && (
                <div
                  className="flex flex-col gap-2 py-2"
                  data-testid="void-picker-step"
                >
                  <p className="text-sm font-medium">
                    {t("collabs.no_ship.dialog.pick_title")}
                  </p>
                  <RadioGroup
                    value={productId}
                    onValueChange={setProductId}
                    aria-label={t("collabs.no_ship.dialog.pick_title")}
                    className="flex flex-col gap-2"
                  >
                    {offer!.map((product) => {
                      const id = `void-picker-product-${product.id}`
                      return (
                        <div
                          key={product.id}
                          data-testid="void-picker-product-option"
                          data-exhausted={product.exhausted ? "true" : "false"}
                          className="flex min-w-0 items-center gap-3 rounded-md border p-2"
                        >
                          <RadioGroupItem
                            value={String(product.id)}
                            id={id}
                            className="shrink-0"
                            aria-label={product.name}
                          />
                          <Label
                            htmlFor={id}
                            className="min-w-0 flex-1 cursor-pointer font-normal wrap-anywhere"
                          >
                            {product.name}
                            {product.exhausted && (
                              <span className="text-muted-foreground block text-xs">
                                {t("collabs.no_ship.dialog.pick_later_helper")}
                              </span>
                            )}
                          </Label>
                        </div>
                      )
                    })}
                  </RadioGroup>
                </div>
              )}

              {/* ⚠️ F-VOID-VARIANT — the second step. Its own anchor
                  (`void-picker-variant-option`), NOT the request picker's:
                  these are different screens, and an E2E arm aimed at the void
                  must not match the request picker's nodes and pass on the
                  wrong one. The RENDERING is shared (see `VariantStep`) so the
                  title/SKU/unnamed cascade cannot fork; the IDENTITY is not. */}
              {needsPick && step === "variant" && (
                <div
                  className="flex flex-col gap-2 py-2"
                  data-testid="void-picker-variant-step"
                >
                  <p className="text-sm font-medium">
                    {t("collabs.no_ship.dialog.pick_variant_title")}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {t("collabs.no_ship.dialog.pick_variant_body", {
                      product: chosen?.name ?? "",
                    })}
                  </p>
                  <VariantStep
                    product={chosen ?? null}
                    value={variantId}
                    onChange={setVariantId}
                    testId="void-picker-variant-option"
                    ariaLabel={t("collabs.no_ship.dialog.pick_variant_title")}
                  />
                </div>
              )}

              {clientError && (
                <p
                  role="alert"
                  aria-live="polite"
                  className="text-destructive text-sm"
                  data-testid="void-picker-error"
                >
                  {clientError}
                </p>
              )}
              {/*
                In-flight (UX §3.3): the dialog STAYS OPEN, both buttons
                disable, and the confirm label swaps to a spinner. No
                optimistic mutation of the surrounding block — the server
                decides whether the void landed, and the 303 re-renders the
                sheet with the real answer.
              */}
              <AlertDialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-full sm:w-auto"
                  disabled={processing}
                  autoFocus
                  onClick={() => setOpen(false)}
                  data-testid="collab-void-possession-cancel"
                >
                  {t("collabs.no_ship.dialog.cancel")}
                </Button>
                <Button
                  /* ⚠️ `submit` ONLY on the final step. On a multi-variant
                     product the first click must ADVANCE — submitting there
                     posts a product with no variant, `PickResolver` answers
                     `:variant_required`, and the void rolls back. The legacy
                     one-step hatch keeps submitting on the first click because
                     `isFinalStep` is true whenever `needsPick` is false. */
                  type={isFinalStep ? "submit" : "button"}
                  onClick={isFinalStep ? undefined : advance}
                  /*
                   * `default`, NOT `destructive` (UX §3.3) — the outcome is
                   * helpful, not punitive. The creator is unblocking
                   * themselves, and a red button would read as a penalty for
                   * telling the truth about a stale ledger.
                   */
                  variant="default"
                  className="h-11 w-full sm:w-auto"
                  disabled={processing}
                  data-testid="collab-void-possession-confirm"
                >
                  {processing && (
                    <Spinner className="mr-1 size-4" aria-hidden="true" />
                  )}
                  {processing
                    ? t("collabs.no_ship.dialog.working")
                    : t("collabs.no_ship.dialog.confirm")}
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
